import { Injectable } from '@angular/core';

import { ChartCaptureService } from './chart-capture.service';
import { ExportApiService } from './export-api.service';

/**
 * What actually happened to the screenshot. The caller MUST surface this.
 *
 * The whole point of modelling the outcome instead of returning void is that
 * "copied to your clipboard" and "saved to your downloads" send the user to two
 * different places. A capture that quietly does one while the UI implies the
 * other is the same silent-wrong-answer family as a blank PNG: nothing errors,
 * and the user looks in the wrong place and concludes the button is broken.
 */
export type ScreenshotOutcome =
  /** In the clipboard. Paste it. */
  | { kind: 'clipboard' }
  /** The clipboard was unavailable or refused, so it was saved instead. */
  | { kind: 'download'; filename: string; reason: string }
  /** The chart had nothing on it — no image was produced, and none was faked. */
  | { kind: 'empty' }
  /** Capture itself failed. Distinct from `empty`: the chart had data. */
  | { kind: 'failed'; reason: string };

@Injectable({ providedIn: 'root' })
export class ChartScreenshotService {
  constructor(
    private chartCapture: ChartCaptureService,
    private exportApi: ExportApiService
  ) {}

  /**
   * Capture one chart and put it where the user can use it.
   *
   * CLIPBOARD FIRST, because that is what the desktop does — `GraphControl.xaml.cs`
   * is `Clipboard.SetImage(Target.ExportToBitmapSource())` and writes no file at
   * all. Operators paste the chart straight into a report, so a download would
   * make them fish it out of a folder to do the same thing.
   *
   * DOWNLOAD IS A REAL FALLBACK, NOT A FORMALITY. A browser clipboard image write
   * needs a secure context, needs `ClipboardItem` to exist, needs permission, and
   * needs the document focused. It can also fail simply because the capture is
   * async: by the time the image exists the user activation from the click may
   * have lapsed, and Chrome answers that with NotAllowedError. That is a routine
   * outcome here, not an exotic one, which is why the fallback saves the file and
   * the caller says which of the two happened.
   */
  async capture(name: string, filenameBase: string): Promise<ScreenshotOutcome> {
    const base64 = await this.chartCapture.captureVisible(name);
    if (!base64) {
      // Distinguish "nothing to draw" from "drawing failed". hasRenderedData is
      // the only thing that can tell them apart, and it is cheap.
      return this.chartCapture.hasRenderedData(name)
        ? { kind: 'failed', reason: 'the chart did not produce an image' }
        : { kind: 'empty' };
    }

    const blob = base64ToPngBlob(base64);
    const filename = `${filenameBase}.png`;

    const refusal = await this.tryClipboard(blob);
    if (refusal === null) {
      return { kind: 'clipboard' };
    }

    this.exportApi.downloadBlob(blob, filename);
    return { kind: 'download', filename, reason: refusal };
  }

  /**
   * Returns null on success, or a short reason the clipboard could not take it.
   *
   * Every branch returns a REASON rather than a boolean, so the caller can tell
   * the user why they are looking in their downloads folder instead of pasting.
   */
  private async tryClipboard(blob: Blob): Promise<string | null> {
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard || typeof clipboard.write !== 'function') {
      return 'this browser cannot copy images';
    }
    // ClipboardItem is absent in some browsers that still expose navigator.clipboard.
    const Item = (window as unknown as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
    if (!Item) {
      return 'this browser cannot copy images';
    }
    if (!window.isSecureContext) {
      // http:// origins have no clipboard-image access at all.
      return 'the clipboard needs a secure (https) connection';
    }

    try {
      await clipboard.write([new Item({ 'image/png': blob })]);
      return null;
    } catch (err) {
      // Most often NotAllowedError: permission refused, or the click's user
      // activation expired while the capture was running.
      const name = (err as { name?: string })?.name;
      return name === 'NotAllowedError'
        ? 'the browser refused clipboard access'
        : 'the clipboard write failed';
    }
  }
}

/**
 * base64 PNG -> Blob, without a data: URL round-trip.
 *
 * `fetch(dataUrl)` would be shorter and is a trap: the app's CSP governs fetch,
 * and a multi-megabyte data: URL is a needless copy of the whole image as a
 * string. atob + Uint8Array is the direct route.
 */
export function base64ToPngBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'image/png' });
}

/**
 * A filename that says which project and which chart, and sorts by time.
 *
 * Colons are illegal in filenames on Windows, which is what the operators run, so
 * the timestamp is flattened rather than ISO.
 */
export function screenshotFilename(project: string | null | undefined, chartLabel: string): string {
  const stamp = new Date()
    .toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .slice(0, 19);
  const safe = (s: string) => (s || '').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  const parts = [safe(project ?? ''), safe(chartLabel), stamp].filter(Boolean);
  return parts.join('_');
}
